import Link from "@/src/common/components/Link"
import classNames from "classnames"
import { ReactNode } from "react"

import { useMenu } from "./hooks"

const POSITION_SETTINGS = {
  "top-left": "left-0 bottom-3",
  "top-right": "right-0 bottom-3",
  "top-center": "left-1/2 transform -translate-x-1/2 bottom-3 bottom-3",
  "bottom-left": "left-0 top-1",
  "bottom-right": "right-0 top-1",
  "bottom-center": "left-1/2 transform -translate-x-1/2 top-1"
}

export type MenuProps = {
  children: ReactNode
  position?: keyof typeof POSITION_SETTINGS
  labelledBy?: string
  visible: boolean
  columns?: "one" | "three"
}

const Menu = (props: MenuProps) => {
  const {
    children,
    position = "bottom-left",
    labelledBy,
    visible = false,
    columns = "one"
  } = props
  const { menuParentRef, keyboardHandler } = useMenu(visible)

  const positionClass = POSITION_SETTINGS[position]

  let colsClass = ""
  if (columns === "three") {
    colsClass = "grid grid-cols-[repeat(3,250px)] gap-0"
  }
  return (
    <div className="relative">
      <div
        ref={menuParentRef}
        className={classNames(
          `z-[3] absolute mt-2 rounded-md shadow-lg bg-white focus:outline-none dark:border-[0.5px] dark:border-gray-600 dark:rounded-none ${positionClass}`,
          "dark:md:-mt-1 dark:min-w-full",
          "ring-1 ring-black ring-opacity-5 dark:mt-0 dark:ring-0"
        )}
        style={{ display: visible ? "" : "none" }}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby={labelledBy}
        onKeyDown={keyboardHandler}
      >
        <div
          className={`max-h-[278px] tablet:max-h-[178px] overflow-hidden overflow-y-auto w-full py-1" ${colsClass}`}
          role="none"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

type MenuItemProps = {
  children?: string
}

type MenuLink = MenuItemProps & {
  href: string
  icon?: ReactNode
  onClick?: () => void
  className?: string
}

const MENU_ITEM_CLASS =
  "!text-stears_black outline-none block w-full text-left px-4 py-2 dark:p-2.5 text-sm hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:ring-gray-200 focus-visible:ring-1 focus-visible:ring-offset-2 flex items-center dark:bg-[#1A1D21] hover:no-underline dark:hover:no-underline  dark:!text-stears_white dark:hover:bg-gray-600"

const FLEX_CENTER = "flex items-center justify-center"

const MenuLink = (props: MenuLink) => {
  const { children, href, icon, className, onClick } = props
  return (
    <Link legacyBehavior href={href}>
      <a
        onClick={onClick}
        className={`${MENU_ITEM_CLASS} ${className}`}
        role="menuitem"
        tabIndex={-1}
      >
        {icon && <span className={`mr-2.5 ${FLEX_CENTER}`}>{icon}</span>}
        <span>{children}</span>
      </a>
    </Link>
  )
}

type MenuButton = MenuItemProps & {
  onClick?: () => void
  icon?: ReactNode
}

const Button = (props: MenuButton) => {
  const { children, onClick, icon } = props
  return (
    <button
      onClick={onClick}
      className={MENU_ITEM_CLASS}
      role="menuitem"
      tabIndex={-1}
      type="button"
    >
      {icon && <span className={`mr-2.5 ${FLEX_CENTER}`}>{icon}</span>}
      <span>{children}</span>
    </button>
  )
}

Button.displayName = "Menu.Button"
MenuLink.displayName = "Menu.Link"

Menu.Button = Button
Menu.Link = MenuLink

export default Menu
